import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { LIVE_LINK } from "../helpers/url_helper";
import { socket } from "../app-socket/socket";
import { WsEvents } from "../app-socket/events";

function normalizeSocketPayload(res) {
  // Server sometimes sends [updatedTransfer, agents]; make it explicit
  if (Array.isArray(res)) {
    const [transfer, agents, fees, boxSizes, managers] = res;
    return { transfer, agents, fees, boxSizes, managers };
  }
  return res; // already an object like { transfer, agents, ... } or just { ...transfer }
}
function applyTransferAddToDraft(draft, payload) {
  const { transfer, agents, fees, boxSizes, managers } = normalizeSocketPayload(payload);
  if (!transfer?._id) return;

  // A) Endpoint returns an array of transfers
  if (Array.isArray(draft)) {
    const exists = draft.some((t) => t._id === transfer._id);
    if (!exists) draft.push(transfer);
    return;
  }

  // B) Endpoint returns an object like { transfers, agents, ... }
  if (draft && Array.isArray(draft.transfers)) {
    const exists = draft.transfers.some((t) => t._id === transfer._id);
    if (!exists) draft.transfers.push(transfer);
  }

  // (Optional) Replace auxiliary lists only if provided
  if (Array.isArray(agents)) draft.agents = agents;
  if (Array.isArray(fees)) draft.fees = fees;
  if (Array.isArray(boxSizes)) draft.boxSizes = boxSizes;
  if (Array.isArray(managers)) draft.managers = managers;
}
function applyTransferUpdateToDraft(draft, payload) {
  const { transfer, agents, fees, boxSizes, managers } = normalizeSocketPayload(payload);

  // 1) If the draft is an array of transfers
  if (Array.isArray(draft)) {
    if (transfer?._id) {
      const i = draft.findIndex((t) => t._id === transfer._id);
      if (i !== -1) draft[i] = transfer;
      else draft.unshift(transfer); // or ignore, depending on your rules
    }
    return;
  }

  // 2) If the draft is an object like { transfers, agents, fees, ... }
  if (draft && Array.isArray(draft.transfers)) {
    if (transfer?._id) {
      const i = draft.transfers.findIndex((t) => t._id === transfer._id);
      if (i !== -1) draft.transfers[i] = transfer;
      else draft.transfers.unshift(transfer);
    }
  }

  // Replace auxiliary lists only if they were provided
  if (Array.isArray(agents)) draft.agents = agents;
  if (Array.isArray(fees)) draft.fees = fees;
  if (Array.isArray(boxSizes)) draft.boxSizes = boxSizes;
  if (Array.isArray(managers)) draft.managers = managers;
}
function applyTransferDeleteToDraft(draft, payload) {
  console.log(payload);
  // Accept either {_id}, {id}, or the id string itself
  const id = payload[0]?._id || payload[0]?.id || payload[0];
  if (!id) return;
  // A) If endpoint returns an array of transfers
  if (Array.isArray(draft)) {
    const i = draft.findIndex((t) => t._id === id);
    if (i !== -1) draft.splice(i, 1);
    return;
  }

  // B) If endpoint returns an object like { transfers, agents, ... }
  if (draft && Array.isArray(draft.transfers)) {
    const i = draft.transfers.findIndex((t) => t._id === id);
    if (i !== -1) draft.transfers.splice(i, 1);
  }
}
// This service handles the API interactions for logistic fees, specifically for deliveries.
export const transfersEuropeApi = createApi({
  reducerPath: "transfersEuropeApi",
  baseQuery: fetchBaseQuery({ baseUrl: `${LIVE_LINK}/transfers` }),
  tagTypes: ["transfers", "TransferLogs"],
  endpoints: (build) => ({
    fetchLatestTransfers: build.query({
      query: (id) => ({ url: `/${id}` }),
      providesTags: () => [{ type: "transfers", id: "LIST" }],
      async onCacheEntryAdded(arg, { updateCachedData, cacheDataLoaded, cacheEntryRemoved }) {
        await cacheDataLoaded;

        // ⬇️ Only use socket when viewing ALL orders
        if (arg !== "all") {
          // Keep this entry tidy: wait for unsubscribe and return
          await cacheEntryRemoved;
          return;
        }

        const onAdded = (res) => {
          if (res && !("error" in res)) {
            updateCachedData((draft) => applyTransferAddToDraft(draft, res));
          }
        };

        const onUpdated = (res) => {
          if ("error" in res) return;
          updateCachedData((draft) => applyTransferUpdateToDraft(draft, res));
        };

        const onDeleted = (res) => {
          if ("error" in res) return;
          updateCachedData((draft) => applyTransferDeleteToDraft(draft, res));
        };
        socket.on(WsEvents.transfer.ON_ADDED, onAdded);
        socket.on(WsEvents.transfer.ON_UPDATED, onUpdated);
        socket.on(WsEvents.transfer.ON_DELETED, onDeleted);

        await cacheEntryRemoved;

        socket.off(WsEvents.transfer.ON_ADDED, onAdded);
        socket.off(WsEvents.transfer.ON_UPDATED, onUpdated);
        socket.off(WsEvents.transfer.ON_DELETED, onDeleted);
      },
    }),
    filterTransfers: build.query({
      query: ({ id, filterFormValues }) => ({
        url: "/status",
        method: "POST",
        body: { id, filterFormValues },
      }),
      providesTags: () => [{ type: "transfers", id: "LIST" }],
      async onCacheEntryAdded(arg, { updateCachedData, cacheDataLoaded, cacheEntryRemoved }) {
        await cacheDataLoaded;

        // ⬇️ Only use socket when viewing ALL orders
        if (arg !== "all") {
          // Keep this entry tidy: wait for unsubscribe and return
          await cacheEntryRemoved;
          return;
        }

        const onAdded = (res) => {
          if (res && !("error" in res)) {
            updateCachedData((draft) => applyTransferAddToDraft(draft, res));
          }
        };

        const onUpdated = (res) => {
          if ("error" in res) return;
          updateCachedData((draft) => applyTransferUpdateToDraft(draft, res));
        };

        const onDeleted = (res) => {
          if ("error" in res) return;
          updateCachedData((draft) => applyTransferDeleteToDraft(draft, res));
        };
        socket.on(WsEvents.transfer.ON_ADDED, onAdded);
        socket.on(WsEvents.transfer.ON_UPDATED, onUpdated);
        socket.on(WsEvents.transfer.ON_DELETED, onDeleted);

        await cacheEntryRemoved;

        socket.off(WsEvents.transfer.ON_ADDED, onAdded);
        socket.off(WsEvents.transfer.ON_UPDATED, onUpdated);
        socket.off(WsEvents.transfer.ON_DELETED, onDeleted);
      },
    }),
    getLogsByOrderId: build.query({
      query: (orderId) => ({
        url: `/logbook/${orderId}`,
        method: "GET",
      }),
    }),
  }),
});
